export type RequestParams = Record<string, unknown>;
export type ApiResponse = Record<string, unknown>;

export type CacheEntry<T> = {
  expiresAt: number;
  value: T;
};

const normalizeForKey = (value: unknown, visited: WeakSet<object>): unknown => {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    return '[FormData]';
  }

  if (visited.has(value)) {
    return '[Circular]';
  }

  visited.add(value);

  if (Array.isArray(value)) {
    const normalized = value.map(item => normalizeForKey(item, visited));
    visited.delete(value);
    return normalized;
  }

  const source = value as Record<string, unknown>;
  const normalized = Object.keys(source)
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      const current = source[key];
      if (typeof current !== 'undefined') {
        acc[key] = normalizeForKey(current, visited);
      }
      return acc;
    }, {});
  visited.delete(value);
  return normalized;
};

export const buildRequestCacheKey = (url: string, params: unknown): string =>
  `${url}:${JSON.stringify(normalizeForKey(params, new WeakSet<object>()))}`;

export const readTtlCache = <T>(cache: Map<string, CacheEntry<T>>, key: string): T | null => {
  const cached = cache.get(key);
  if (!cached) {
    return null;
  }

  if (cached.expiresAt <= Date.now()) {
    cache.delete(key);
    return null;
  }

  return cached.value;
};

export const writeTtlCache = <T>(cache: Map<string, CacheEntry<T>>, key: string, value: T, ttlMs: number): void => {
  cache.set(key, {
    expiresAt: Date.now() + ttlMs,
    value,
  });
};

export const withInflightDedupe = <T>(
  inflight: Map<string, Promise<T>>,
  key: string,
  requestFactory: () => Promise<T>,
): Promise<T> => {
  const existing = inflight.get(key);
  if (existing) {
    return existing;
  }

  // 同参数请求在同一时间只放行一次，避免输入/初始化阶段把后台打成并发重复请求。
  const request = requestFactory().finally(() => {
    inflight.delete(key);
  });
  inflight.set(key, request);
  return request;
};
